import _ from 'lodash'

export const useAshemaletube = () => {
	const {getFileNameFromUrl, extFetch} = useHelpers()
	/**
	 * Extract video URL from ashemaletube page
	 * Supports both direct video URLs and page URLs
	 */
	async function getVideoFromUrl(url) {
		try {
			// If it's already a direct video URL, return it
			if (url.match(/\.(mp4|webm|m3u8)(\?|$)/i)) {
				return {
					website: 'ashemaletube.com',
					type: 'video',
					url: url,
					fileName: getFileNameFromUrl(url) || 'video.mp4',
					previewUrl: url,
					thumbnail: null,
					canView: true
				}
			}

			// Otherwise, try to fetch the page and extract video URL
			try {
				let response = await extFetch(url, {
					method: 'GET',
					headers: {
						'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
					}
				})

				if (response && response.body) {
					// If response is HTML, parse it
					let html = typeof response.body === 'string' ? response.body : (response.body.toString ? response.body.toString() : String(response.body))
					
					// Try to extract video URLs from the sources array in script tags
					// Pattern: var sources = [{...}];
					// Helper function to extract the complete array by counting brackets
					function extractSourcesArray(text) {
						// Find the start of the sources array assignment
						let startMatch = text.match(/(?:var\s+)?sources\s*=\s*\[/i)
						if (!startMatch) return null
						
						let startIdx = startMatch.index + startMatch[0].length - 1 // Position of '['
						let bracketCount = 0
						let inString = false
						let escapeNext = false
						
						for (let i = startIdx; i < text.length; i++) {
							let char = text[i]
							
							if (escapeNext) {
								escapeNext = false
								continue
							}
							
							if (char === '\\') {
								escapeNext = true
								continue
							}
							
							if (char === '"' && !escapeNext) {
								inString = !inString
								continue
							}
							
							if (!inString) {
								if (char === '[') bracketCount++
								if (char === ']') {
									bracketCount--
									if (bracketCount === 0) {
										// Found the complete array
										let arrayStr = text.substring(startIdx, i + 1)
										try {
											let sources = JSON.parse(arrayStr)
											return Array.isArray(sources) ? sources : null
										} catch (e) {
											return null
										}
									}
								}
							}
						}
						return null
					}
					
					// First, try to find script tags and search within them
					let scriptMatches = html.match(/<script[^>]*>([\s\S]*?)<\/script>/gi)
					if (scriptMatches) {
						for (let scriptContent of scriptMatches) {
							let contentMatch = scriptContent.match(/<script[^>]*>([\s\S]*?)<\/script>/i)
							if (contentMatch && contentMatch[1]) {
								let sources = extractSourcesArray(contentMatch[1])
								if (sources && sources.length > 0) {
									// Find the best quality video
									// Prefer active one, then highest quality (1080p > 720p > 480p > 360p)
									let selectedSource = sources.find(s => s.active === 'true') || 
														  sources.find(s => s.desc === '1080p') ||
														  sources.find(s => s.desc === '720p') ||
														  sources.find(s => s.desc === '480p') ||
														  sources[0] // Fallback to first
									
									if (selectedSource && selectedSource.src) {
										let videoUrl = selectedSource.src
										let isHLS = selectedSource.hls === true || videoUrl.includes('.m3u8')
										
										return {
											website: 'ashemaletube.com',
											type: 'video',
											url: videoUrl,
											fileName: getFileNameFromUrl(videoUrl) || (isHLS ? 'video.mp4' : 'video.mp4'),
											previewUrl: videoUrl,
											thumbnail: null,
											canView: true,
											quality: selectedSource.desc || 'unknown',
											hls: isHLS
										}
									}
								}
							}
						}
					}
					
					// Fallback: Try direct extraction on entire HTML
					let sources = extractSourcesArray(html)
					if (sources && sources.length > 0) {
						let selectedSource = sources.find(s => s.active === 'true') || 
											  sources.find(s => s.desc === '1080p') ||
											  sources.find(s => s.desc === '720p') ||
											  sources.find(s => s.desc === '480p') ||
											  sources[0]
						
						if (selectedSource && selectedSource.src) {
							let videoUrl = selectedSource.src
							let isHLS = selectedSource.hls === true || videoUrl.includes('.m3u8')
							
							return {
								website: 'ashemaletube.com',
								type: 'video',
								url: videoUrl,
								fileName: getFileNameFromUrl(videoUrl) || (isHLS ? 'video.mp4' : 'video.mp4'),
								previewUrl: videoUrl,
								thumbnail: null,
								canView: true,
								quality: selectedSource.desc || 'unknown',
								hls: isHLS
							}
						}
					}
					
					// Fallback: Try to find video source in various formats
					// Look for video tags, source tags, and common video URL patterns
					let videoMatch = html.match(/<video[^>]*src=["']([^"']+)["']/i) || 
									  html.match(/<source[^>]*src=["']([^"']+)["']/i) ||
									  html.match(/videoUrl["']?\s*[:=]\s*["']([^"']+)["']/i) ||
									  html.match(/video_url["']?\s*[:=]\s*["']([^"']+)["']/i) ||
									  html.match(/["'](https?:\/\/[^"']+\.(mp4|webm|m3u8)[^"']*)["']/i) ||
									  html.match(/src\s*[:=]\s*["']([^"']+\.(mp4|webm|m3u8)[^"']*)["']/i)
					
					if (videoMatch && videoMatch[1]) {
						let videoUrl = videoMatch[1]
						// Make absolute URL if relative
						if (videoUrl.startsWith('//')) {
							videoUrl = 'https:' + videoUrl
						} else if (videoUrl.startsWith('/')) {
							let urlObj = new URL(url)
							videoUrl = urlObj.origin + videoUrl
						} else if (!videoUrl.startsWith('http')) {
							let urlObj = new URL(url)
							videoUrl = urlObj.origin + '/' + videoUrl
						}

						return {
							website: 'ashemaletube.com',
							type: 'video',
							url: videoUrl,
							fileName: getFileNameFromUrl(videoUrl) || 'video.mp4',
							previewUrl: videoUrl,
							thumbnail: null,
							canView: true
						}
					}
				}
			} catch (fetchError) {
				console.warn('Could not fetch page to extract video URL:', fetchError)
				// Continue to fallback
			}

			// Fallback: return the original URL as-is (might be a direct link)
			return {
				website: 'ashemaletube.com',
				type: 'video',
				url: url,
				fileName: getFileNameFromUrl(url) || 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true
			}
		} catch (error) {
			console.error('Error fetching video from URL:', error)
			// Return a basic media object even on error
			return {
				website: 'ashemaletube.com',
				type: 'video',
				url: url,
				fileName: getFileNameFromUrl(url) || 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true
			}
		}
	}

	/**
	 * Process multiple URLs and extract videos
	 */
	async function getVideosFromUrls(urls) {
		let validUrls = urls
			.split('\n')
			.map(url => url.trim())
			.filter(url => url && url.length > 0)

		const CONCURRENCY = 5
		const results = new Array(validUrls.length)
		let nextIndex = 0

		const worker = async () => {
			while (nextIndex < validUrls.length) {
				const idx = nextIndex++
				results[idx] = await getVideoFromUrl(validUrls[idx])
			}
		}

		await Promise.all(
			Array.from({length: Math.min(CONCURRENCY, validUrls.length)}, () => worker())
		)

		return results.map((media, index) => ({
			...media,
			id: `ashemaletube-${Date.now()}-${index}`,
			source: 'ashemaletube'
		}))
	}

	return {
		getMedia: async (form) => {
			if (form.urls) return getVideosFromUrls(form.urls)
			if (form.url) {
				let media = await getVideoFromUrl(form.url)
				media.id = `ashemaletube-${Date.now()}`
				media.source = 'ashemaletube'
				return [media]
			}
			return []
		},
		getVideoFromUrl,
		getVideosFromUrls
	}
}
